import type { ContactInfo, CreateProjectParams, SlaOverrides, UpdateProjectParams } from '@/domain/models/project.js';
import type { FormState } from '@/presentation/pages/projects/project-form/utils/form-state.js';

export function buildContactInfo(name: string, email: string, phone: string): ContactInfo | undefined {
    if (!name && !email && !phone) {
        return undefined;
    }
    return { name: name || null, email: email || null, phone: phone || null };
}

export function buildSlaOverrides(form: FormState): SlaOverrides | undefined {
    const hasOverride =
        form.overrideSlaEstimateEf ||
        form.overrideSlaStartDev ||
        form.overrideSlaAnalyzeBug ||
        form.overrideSlaAnalyzeMinor ||
        form.overrideSlaDaysValidateDoc ||
        form.overrideSlaMinScoreAi ||
        form.overrideAiStrictnessLevel ||
        form.overrideAiExtraSystemPrompt;
    if (!hasOverride) {
        return undefined;
    }
    return {
        slaHoursEstimateEf: form.overrideSlaEstimateEf ? Number(form.slaHoursEstimateEf) : null,
        slaHoursStartDev: form.overrideSlaStartDev ? Number(form.slaHoursStartDev) : null,
        slaHoursAnalyzeBug: form.overrideSlaAnalyzeBug ? Number(form.slaHoursAnalyzeBug) : null,
        slaHoursAnalyzeMinor: form.overrideSlaAnalyzeMinor ? Number(form.slaHoursAnalyzeMinor) : null,
        slaDaysValidateDoc: form.overrideSlaDaysValidateDoc ? Number(form.slaDaysValidateDoc) : null,
        slaMinScoreAi: form.overrideSlaMinScoreAi ? Number(form.slaMinScoreAi) : null,
        aiStrictnessLevel: form.overrideAiStrictnessLevel ? Number(form.aiStrictnessLevel) : null,
        aiExtraSystemPrompt: form.overrideAiExtraSystemPrompt ? form.aiExtraSystemPrompt.trim() : null
    };
}

export function buildCreateProjectParams(form: FormState): CreateProjectParams {
    const clientPm = buildContactInfo(form.clientPmName, form.clientPmEmail, form.clientPmPhone);
    const agilePm = buildContactInfo(form.agilePmName, form.agilePmEmail, form.agilePmPhone);
    const slaOverrides = buildSlaOverrides(form);
    const aiModel = form.overrideAiModel ? form.aiModel || null : null;
    return {
        name: form.name.trim(),
        abbreviation: form.abbreviation.toUpperCase(),
        clientId: form.clientId,
        description: form.description || null,
        clientPm,
        agilePm,
        slaOverrides,
        aiModel,
        jiraConfig: {
            jiraProjectKey: form.jiraProjectKey.trim() || null,
            jiraBaseUrl: form.jiraBaseUrl.trim() || null
        },
        reportsUrl: form.reportsUrl.trim() || null
    };
}

export function buildUpdateProjectParams(form: FormState): UpdateProjectParams {
    const clientPm = buildContactInfo(form.clientPmName, form.clientPmEmail, form.clientPmPhone);
    const agilePm = buildContactInfo(form.agilePmName, form.agilePmEmail, form.agilePmPhone);
    const slaOverrides = buildSlaOverrides(form);
    const aiModel = form.overrideAiModel ? form.aiModel || null : null;
    return {
        name: form.name.trim(),
        abbreviation: form.abbreviation.toUpperCase(),
        description: form.description || null,
        clientPm,
        agilePm,
        slaOverrides,
        aiModel,
        jiraConfig: {
            jiraProjectKey: form.jiraProjectKey.trim() || null,
            jiraBaseUrl: form.jiraBaseUrl.trim() || null
        },
        reportsUrl: form.reportsUrl.trim() || null
    };
}
